import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { extraescolarRoutes } from "../lib/extraescolares";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [campamentosOpen, setCampamentosOpen] = useState(false);
  const [extraescolaresOpen, setExtraescolaresOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCampamentosOpen(false);
    setExtraescolaresOpen(false);
  }, [location]);

  const campamentoLinks = [
    { label: "Campamentos de verano", href: "/campamento-verano" },
    { label: "Campamento Urbano", href: "/campamento-verano/campamento-urbano" },
  ];

  const navLinks = [
    { label: "Encuentro Adultos", href: "/encuentro-adultos" },
    { label: "Equipo", href: "/equipo" },
    { label: "FAQ", href: "/faq" },
    { label: "Contacto", href: "/contacto" },
  ];

  const isActive = (href) => location.pathname === href;
  const isCampamentosActive = location.pathname.startsWith("/campamento-verano");
  const isExtraescolaresActive = location.pathname.startsWith("/cursos-especializados");

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || menuOpen
          ? "bg-white/95 backdrop-blur-xl shadow-sm"
          : "bg-white/80 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center group">
            <img
              src="/images/logos/tagline-negro.png"
              alt="DAC-PAC · De altas capacidades, para altas capacidades"
              className="object-contain"
              style={{ height: "40px", width: "auto", mixBlendMode: "multiply" }}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            <div
              className="relative"
              onMouseEnter={() => setCampamentosOpen(true)}
              onMouseLeave={() => setCampamentosOpen(false)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setCampamentosOpen(false);
              }}
            >
              <button
                type="button"
                onFocus={() => setCampamentosOpen(true)}
                onClick={() => setCampamentosOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={campamentosOpen}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 inline-flex items-center gap-1.5 ${
                  isCampamentosActive
                    ? "text-slate-900 bg-slate-100"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Campamentos
                <ChevronDown size={14} aria-hidden="true" />
              </button>
              {campamentosOpen && (
                <div className="absolute left-0 top-full w-64 pt-2">
                  <div
                    role="menu"
                    className="rounded-2xl bg-white p-2 shadow-xl border border-slate-100"
                  >
                    {campamentoLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        role="menuitem"
                        onClick={() => setCampamentosOpen(false)}
                        className={`block rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                          isActive(link.href)
                            ? "text-slate-900 bg-slate-100"
                            : "text-slate-700 hover:bg-slate-50 hover:text-slate-950"
                        }`}
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div
              className="relative"
              onMouseEnter={() => setExtraescolaresOpen(true)}
              onMouseLeave={() => setExtraescolaresOpen(false)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setExtraescolaresOpen(false);
              }}
            >
              <button
                type="button"
                onFocus={() => setExtraescolaresOpen(true)}
                onClick={() => setExtraescolaresOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={extraescolaresOpen}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 inline-flex items-center gap-1.5 ${
                  isExtraescolaresActive
                    ? "text-slate-900 bg-slate-100"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Extraescolares
                <ChevronDown size={14} aria-hidden="true" />
              </button>
              {extraescolaresOpen && (
                <div className="absolute left-0 top-full w-72 pt-2">
                  <div
                    role="menu"
                    className="rounded-2xl bg-white p-2 shadow-xl border border-slate-100"
                  >
                    <Link
                      to="/cursos-especializados"
                      role="menuitem"
                      onClick={() => setExtraescolaresOpen(false)}
                      className={`block rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                        isActive("/cursos-especializados")
                          ? "text-slate-900 bg-slate-100"
                          : "text-slate-700 hover:bg-slate-50 hover:text-slate-950"
                      }`}
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      Ver todas
                    </Link>
                    <div className="my-1 border-t border-slate-100" />
                    {extraescolarRoutes.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        role="menuitem"
                        onClick={() => setExtraescolaresOpen(false)}
                        className={`block rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                          isActive(link.href)
                            ? "text-slate-900 bg-slate-100"
                            : "text-slate-700 hover:bg-slate-50 hover:text-slate-950"
                        }`}
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        {link.shortLabel}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.replace("/", "")}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive(link.href)
                    ? "text-slate-900 bg-slate-100"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/preinscripcion"
              data-testid="nav-preinscripcion-cta"
              className="ml-3 text-sm rounded-full font-semibold"
              style={{ padding: "0.55rem 1.4rem", background: "#111827", color: "white", fontFamily: "Manrope, sans-serif", display: "inline-flex", alignItems: "center", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#1E293B"}
              onMouseLeave={e => e.currentTarget.style.background = "#111827"}
            >
              Preinscripción
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-slate-100 bg-white max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            <div className="px-4 pt-2 pb-1 text-xs font-bold uppercase tracking-widest text-slate-400" style={{ fontFamily: "Manrope, sans-serif" }}>
              Campamentos
            </div>
            {campamentoLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? "text-slate-900 bg-slate-100"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                {link.label}
              </Link>
            ))}
            <div className="px-4 pt-4 pb-1 text-xs font-bold uppercase tracking-widest text-slate-400" style={{ fontFamily: "Manrope, sans-serif" }}>
              Extraescolares
            </div>
            <Link
              to="/cursos-especializados"
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                isActive("/cursos-especializados")
                  ? "text-slate-900 bg-slate-100"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Ver todas
            </Link>
            <div className="max-h-64 overflow-y-auto pr-1 rounded-xl border border-slate-100 bg-slate-50/60">
              {extraescolarRoutes.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block px-4 py-3 text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? "text-slate-900 bg-slate-100"
                      : "text-slate-700 hover:bg-white"
                  }`}
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {link.shortLabel}
                </Link>
              ))}
            </div>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? "text-slate-900 bg-slate-100"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/preinscripcion"
              className="text-sm mt-2 justify-center rounded-full font-semibold py-3 px-4 text-center"
              style={{ background: "#111827", color: "white", fontFamily: "Manrope, sans-serif", display: "block" }}
            >
              Preinscripción
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
